import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { CircleIcon } from "@phosphor-icons/react/dist/csr/Circle";
import { Progress } from "@volca/ui/components/ui/progress";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { APPROVAL_PALETTE } from "@/core/colors";
import type {
  IPayrollSheetApprovalAccessKey,
  SheetApprovalType,
} from "@/payroll/payroll.d";
import { getApprovalGate } from "./approval-types";

export const GroupApprovalProgress = ({
  accessKey,
  approvalType,
  variant = "compact",
}: {
  accessKey: IPayrollSheetApprovalAccessKey;
  approvalType: SheetApprovalType;
  variant?: "compact" | "bar";
}) => {
  const status = accessKey.approvals[approvalType];
  const approved = status.approved_count ?? 0;
  const total = status.total_count ?? 0;
  const label = `${getApprovalGate(approvalType).label} for ${accessKey.name}`;
  if (variant === "bar") {
    if (total <= 1) return null;

    return (
      <Stack className="w-full" direction="row" align="center" gap={2}>
        <Progress
          value={total ? (approved / total) * 100 : 0}
          aria-label={label}
          aria-valuetext={`${approved} of ${total} sheets approved`}
          className="flex-1 *:data-[slot=progress-track]:bg-line"
        />
        <Text variant="caption" tone="muted" className="whitespace-nowrap">
          {approved} of {total} sheets approved
        </Text>
      </Stack>
    );
  }
  const Icon = status.approved ? CheckCircleIcon : CircleIcon;
  const count = `${approved}/${total}`;
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      role="status"
      aria-label={`${label}: ${count} sheets approved${status.approved ? ", approved" : ", not approved"}`}
    >
      <Text weight="semibold" variant="caption">
        {count}
      </Text>
      <Icon
        aria-hidden="true"
        className={cn("h-3.5 w-3.5", {
          [APPROVAL_PALETTE.approved.foreground]: status.approved,
          "text-subtle": !status.approved,
        })}
      />
    </Stack>
  );
};
